import { useQuery } from '@tanstack/react-query';
import { Grid3X3, List, Mail, MessageSquare, Filter, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { partnerApi, type CommunicationGroup, type CommunicationsFilters } from '../../lib/api';
import { FilterPanel } from '../../components/FilterPanel';
import { PageHeader } from '../../components/common/PageHeader';
import { PaginationBar } from '../../components/common/PaginationBar';

export function CommunicationsPage() {
  const navigate = useNavigate();
  const [communicationType, setCommunicationType] = useState<'email' | 'sms'>('email');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<CommunicationsFilters>({
    type: 'email',
    page: 1,
    limit: 50,
  });

  // Fetch communications with filters
  const {
    data: communicationsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['communications', communicationType, page, filters],
    queryFn: () =>
      partnerApi.getCommunications({
        ...filters,
        type: communicationType,
        page,
      }),
  });

  // Fetch businesses for filter dropdown
  const { data: businessesData, isLoading: businessesLoading } = useQuery({
    queryKey: ['businesses'],
    queryFn: () => partnerApi.getBusinesses(),
  });

  const communications = communicationsData?.communications || [];
  const pagination = communicationsData?.pagination;
  const businesses = businessesData || [];

  const handleCommunicationTypeChange = (type: 'email' | 'sms') => {
    setCommunicationType(type);
    setPage(1);
    setFilters(prev => ({ ...prev, type, page: 1 }));
  };

  const handleSelectCommunication = (communication: CommunicationGroup) => {
    // Build URL with current filters
    const params = new URLSearchParams();
    params.set('type', communicationType);
    params.set('page', page.toString());
    params.set('limit', (filters.limit || 50).toString());

    if (filters.businesses) params.set('businesses', filters.businesses);
    if (filters.platforms) params.set('platforms', filters.platforms);
    if (filters.automationType) params.set('automationType', filters.automationType);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.search) params.set('search', filters.search);

    navigate(`/dashboard/communications/${communication.communicationGroupId}?${params.toString()}`);
  };

  const handleFilterChange = (key: string, value: string | string[] | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      type: communicationType,
      page: 1,
      limit: 50,
    });
    setPage(1);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.businesses && `${filters.businesses}`.length > 0) count++;
    if (filters.automationType) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.search) count++;
    return count;
  };

  const removeFilter = (key: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key as keyof CommunicationsFilters];
      return newFilters;
    });
    setPage(1);
  };

  const getFilterLabel = (key: string, value: unknown): string => {
    switch (key) {
      case 'businesses': {
        const businessIds = Array.isArray(value) ? value : [value];
        const businessNames = businessIds
          .map(id => businesses.find(b => b.id === id)?.name)
          .filter(Boolean);
        return businessNames.length > 0 ? `Business: ${businessNames.join(', ')}` : 'Business filter';
      }
      case 'automationType':
        if (value === 'one_time') return 'Campaign: Broadcasts';
        if (value === 'trigger' || value === 'trigger_based') return 'Campaign: Automation';
        if (value === 'date_based') return 'Campaign: Date-based';
        return 'Campaign filter';
      case 'startDate':
        return `From: ${new Date(value as string).toLocaleDateString()}`;
      case 'endDate':
        return `To: ${new Date(value as string).toLocaleDateString()}`;
      case 'search':
        return `Search: "${value}"`;
      default:
        return `${key}: ${value}`;
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-2xl font-medium text-gray-900 font-nav">Error loading communications</h2>
          <p className="text-gray-600">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4">
        <PageHeader
          title="Communications"
          description="All completed email and SMS communications by your tenants."
          right={
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {pagination && (
                  <>
                    <span className="font-semibold text-gray-900">
                      {pagination.total.toLocaleString()}
                    </span>{' '}
                    total {communicationType === 'email' ? 'emails' : 'SMS messages'}
                  </>
                )}
              </div>
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                {filtersOpen ? (
                  <>
                    <X className="w-4 h-4" />
                    Close Filters
                  </>
                ) : (
                  <>
                    <Filter className="w-4 h-4" />
                    Filters
                    {getActiveFilterCount() > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded-full">
                        {getActiveFilterCount()}
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>
          }
          below={
            <div className="flex items-center gap-4">
              {/* Communication Type Toggle */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => handleCommunicationTypeChange('email')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    communicationType === 'email'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}>
                  <Mail className="w-4 h-4 inline mr-1.5" />
                  Email
                </button>
                <button
                  onClick={() => handleCommunicationTypeChange('sms')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    communicationType === 'sms'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}>
                  <MessageSquare className="w-4 h-4 inline mr-1.5" />
                  SMS
                </button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Table view">
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Grid view">
                  <Grid3X3 className="w-4 h-4" />
                </button>
              </div>
            </div>
          }
        />
      </div>

      {/* Active Filters Display */}
      {getActiveFilterCount() > 0 && (
        <div className="px-6 pb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-blue-900">
                Active Filters ({getActiveFilterCount()})
              </h3>
              <button
                onClick={handleResetFilters}
                className="text-xs font-medium text-blue-700 hover:text-blue-900 underline">
                Clear all filters
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters).map(([key, value]) => {
                // Skip non-filter fields
                if (key === 'type' || key === 'page' || key === 'limit' || !value) {
                  return null;
                }

                return (
                  <span
                    key={key}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-300 rounded-full text-sm text-blue-900 shadow-sm">
                    <span>{getFilterLabel(key, value)}</span>
                    <button
                      onClick={() => removeFilter(key)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                      title="Remove filter">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Filter Overlay */}
      {filtersOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setFiltersOpen(false)}>
          <div
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              businesses={businesses}
              loading={businessesLoading}
            />
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
              <p className="text-gray-600">Loading communications...</p>
            </div>
          </div>
        ) : communications.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No communications found</h2>
              <p className="text-gray-600">Try adjusting your filters or check back later.</p>
            </div>
          </div>
        ) : viewMode === 'table' ? (
          <div className="h-full overflow-auto p-6">
            <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm p-6 transition-opacity duration-200 opacity-100">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {communicationType === 'email' ? 'Subject' : 'Message'}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Business
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipients
                      </th>
                      {communicationType === 'email' && (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Open Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Revenue
                          </th>
                        </>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {communications.map(comm => (
                      <tr
                        key={comm.communicationGroupId}
                        onClick={() => handleSelectCommunication(comm)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {communicationType === 'email' ? comm.emailSubject : comm.messageBody}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{comm.businessName}</div>
                          <div className="text-xs text-gray-500">{comm.platformName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {comm.automationName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {comm.recipientCount.toLocaleString()}
                        </td>
                        {communicationType === 'email' && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {comm.engagementRate}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              $
                              {parseFloat(comm.attributedRevenue || '0').toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}
                            </td>
                          </>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(comm.communicationCreatedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                currentPage={page}
                pageSize={filters.limit ?? 50}
                currentCount={communications.length}
                total={pagination?.total}
                hasNextPage={pagination ? page < pagination.totalPages : false}
                isLoading={isLoading}
                onPrevious={() => setPage(p => Math.max(1, p - 1))}
                onNext={() => setPage(p => (pagination && p < pagination.totalPages ? p + 1 : p))}
              />
            </div>
          </div>
        ) : (
          <div className="h-full overflow-auto p-6">
            <div
              className="grid gap-6 max-w-[1800px] mx-auto"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              }}>
              {communications.map(comm => (
                <CommunicationCard
                  key={comm.communicationGroupId}
                  comm={comm}
                  communicationType={communicationType}
                  onSelect={handleSelectCommunication}
                />
              ))}
            </div>

            <div className="mt-6">
              <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-opacity duration-200 opacity-100 overflow-hidden">
                <PaginationBar
                  currentPage={page}
                  pageSize={filters.limit ?? 50}
                  currentCount={communications.length}
                  total={pagination?.total}
                  hasNextPage={pagination ? page < pagination.totalPages : false}
                  isLoading={isLoading}
                  onPrevious={() => setPage(p => Math.max(1, p - 1))}
                  onNext={() => setPage(p => (pagination && p < pagination.totalPages ? p + 1 : p))}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CommunicationCard({
  comm,
  communicationType,
  onSelect,
}: {
  comm: CommunicationGroup;
  communicationType: 'email' | 'sms';
  onSelect: (comm: CommunicationGroup) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    if (!cardRef.current) return;

    const updateScale = () => {
      if (cardRef.current) {
        const cardWidth = cardRef.current.offsetWidth;
        // iframe is 800px wide, calculate scale to fit card width
        const newScale = cardWidth / 800;
        setScale(newScale);
      }
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(cardRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const getCampaignType = (type?: string | null) => {
    if (!type) return 'Unknown';
    if (type === 'one_time') return 'Broadcast';
    if (type === 'trigger' || type === 'trigger_based') return 'Automation';
    if (type === 'date_based') return 'Date';
    return type;
  };

  return (
    <div
      ref={cardRef}
      onClick={() => onSelect(comm)}
      className="relative rounded-lg overflow-hidden cursor-pointer transition-all border border-border bg-card text-card-foreground shadow-sm group"
      style={{ aspectRatio: '3/4' }}>
      {/* Email/SMS Thumbnail */}
      <div className="absolute inset-0 overflow-hidden">
        {communicationType === 'email' && comm.compiledHtml ? (
          <div className="w-full h-full relative">
            <iframe
              srcDoc={comm.compiledHtml}
              title={comm.emailSubject || 'Email Preview'}
              sandbox="allow-same-origin"
              className="absolute top-0 left-0 border-none pointer-events-none origin-top-left"
              style={{
                width: '800px',
                height: '1200px',
                transform: `scale(${scale})`,
              }}
            />
          </div>
        ) : communicationType === 'sms' ? (
          <div className="w-full h-full flex items-center justify-center p-8 bg-gray-50">
            <div className="bg-card border border-border rounded-2xl p-4 max-w-[80%] text-sm leading-relaxed text-gray-900 line-clamp-8">
              {comm.messageBody?.substring(0, 150) || 'SMS Message'}
              {(comm.messageBody?.length ?? 0) > 150 && '...'}
            </div>
          </div>
        ) : null}
      </div>

      {/* Overlay - slides up on hover */}
      <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border p-4 flex flex-col gap-3 transition-transform duration-200 ease-out translate-y-[calc(100%-48px)] group-hover:translate-y-0">
        {/* Header */}
        <div className="flex justify-between items-start gap-2">
          <h3 className="font-semibold text-sm leading-tight text-gray-900 truncate flex-1 min-w-0">
            {comm.automationName ||
              (communicationType === 'email'
                ? comm.emailSubject || 'Untitled'
                : comm.companyName || 'SMS Message')}
          </h3>
          {comm.automationType && (
            <span className="px-2 py-1 bg-gray-100 text-gray-900 rounded text-[10px] font-semibold uppercase whitespace-nowrap flex-shrink-0">
              {getCampaignType(comm.automationType)}
            </span>
          )}
        </div>

        {/* Metadata - hidden until hover */}
        <div className="flex flex-col gap-1.5 text-[13px] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="flex gap-2">
            <span className="text-gray-500 font-medium">Business:</span>
            <span className="text-gray-900 truncate">{comm.businessName}</span>
          </div>
          {communicationType === 'email' ? (
            <div className="flex gap-2">
              <span className="text-gray-500 font-medium">Subject:</span>
              <span className="text-gray-900 truncate">{comm.emailSubject || 'Untitled'}</span>
            </div>
          ) : (
            comm.automationName && (
              <div className="flex gap-2">
                <span className="text-gray-500 font-medium">Campaign:</span>
                <span className="text-gray-900 truncate">{comm.automationName}</span>
              </div>
            )
          )}
          <div className="flex gap-2">
            <span className="text-gray-500 font-medium">Sent:</span>
            <span className="text-gray-900">
              {new Date(comm.communicationCreatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Stats - hidden until hover */}
        <div className="flex justify-around gap-4 pt-3 border-t border-border opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="flex flex-col items-center gap-1">
            <span className="text-base font-semibold text-gray-900">
              {comm.recipientCount?.toLocaleString()}
            </span>
            <span className="text-[11px] text-gray-500 uppercase tracking-wide">Recipients</span>
          </div>
          {communicationType === 'email' ? (
            <>
              <div className="flex flex-col items-center gap-1">
                <span className="text-base font-semibold text-gray-900">{comm.engagementRate}%</span>
                <span className="text-[11px] text-gray-500 uppercase tracking-wide">Open Rate</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-base font-semibold text-gray-900">
                  $
                  {parseFloat(comm.attributedRevenue || '0').toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </span>
                <span className="text-[11px] text-gray-500 uppercase tracking-wide">Revenue</span>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <span className="text-base font-semibold text-gray-900">{comm.deliveryRate}%</span>
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">Delivery</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
